export type PerformanceWindowName = 'MailComposeCommitToEffect';

export interface PerformanceWindowToken {
    readonly id: number;
    readonly startTime: number;
}

export interface PerformanceWindowAttribution {
    name: PerformanceWindowName;
    startTime: number;
    duration: number | undefined;
    overlapDuration: number;
}

interface PerformanceWindow {
    name: PerformanceWindowName;
    startTime: number;
    endTime?: number;
}

const COMPLETED_WINDOW_RETENTION_MS = 10_000;
const ACTIVE_WINDOW_TIMEOUT_MS = 60_000;

const performanceWindows = new Map<number, PerformanceWindow>();
let nextPerformanceWindowId = 0;

export function startPerformanceWindow(name: PerformanceWindowName): PerformanceWindowToken {
    const currentTime = performance.now();
    prunePerformanceWindows(currentTime);

    const token = { id: ++nextPerformanceWindowId, startTime: currentTime };
    performanceWindows.set(token.id, { name, startTime: currentTime });
    return token;
}

export function endPerformanceWindow(token: PerformanceWindowToken): void {
    const performanceWindow = performanceWindows.get(token.id);
    if (performanceWindow && performanceWindow.endTime === undefined) {
        performanceWindow.endTime = performance.now();
    }
}

export function cancelPerformanceWindow(token: PerformanceWindowToken): void {
    performanceWindows.delete(token.id);
}

export function getPerformanceWindowAttribution(
    startTime: number,
    endTime: number
): PerformanceWindowAttribution | undefined {
    prunePerformanceWindows(performance.now());

    let bestAttribution: PerformanceWindowAttribution | undefined;
    for (const performanceWindow of performanceWindows.values()) {
        const overlapStart = Math.max(startTime, performanceWindow.startTime);
        const overlapEnd = Math.min(endTime, performanceWindow.endTime ?? endTime);
        const overlapDuration = overlapEnd - overlapStart;

        if (overlapDuration > 0 && overlapDuration > (bestAttribution?.overlapDuration ?? 0)) {
            bestAttribution = {
                name: performanceWindow.name,
                startTime: performanceWindow.startTime,
                duration:
                    performanceWindow.endTime === undefined
                        ? undefined
                        : performanceWindow.endTime - performanceWindow.startTime,
                overlapDuration,
            };
        }
    }

    return bestAttribution;
}

function prunePerformanceWindows(currentTime: number): void {
    for (const [id, performanceWindow] of performanceWindows) {
        const expirationTime =
            performanceWindow.endTime === undefined
                ? performanceWindow.startTime + ACTIVE_WINDOW_TIMEOUT_MS
                : performanceWindow.endTime + COMPLETED_WINDOW_RETENTION_MS;
        if (expirationTime < currentTime) {
            performanceWindows.delete(id);
        }
    }
}
